import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BsDisplay, BsGrid, BsList, BsArchiveFill } from 'react-icons/bs';
import { FaHeart, FaDiscord, FaCrown, FaStar } from 'react-icons/fa';
import { FiGlobe, FiSearch, FiClock, FiStar, FiZap, FiCalendar, FiChevronLeft, FiChevronRight, FiPlus } from 'react-icons/fi';
import { MdLanguage, MdPublic } from 'react-icons/md';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import bannerGif from '../../../../assets/images/banner.gif';
import { getServers } from '../../../../store/servers/serversSlice';
import Archive from '../archive';
import {
  PageWrapper, HeroHeader, TabBar, ControlsBar, FilterBtn,
  ServersGrid, ServerCard,
  PaginationBar, PageBtn,
} from './NewServersStyle';
import { backendUrl } from "../../../../utilities/axios";
import { SkeletonCard, NoData } from '../../../shared/WebsiteFeedback';

const PER_PAGE = 8;

const getAgeBadge = (createdAt, t) => {
  if (!createdAt) return null;
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const diffHours = diffMs / 3_600_000;
  const diffDays = diffMs / 86_400_000;
  if (diffHours < 24) return { label: t('website.new_servers.badge_hot'), cls: 'badge-hot' };
  if (diffDays < 3) return { label: t('website.new_servers.badge_new'), cls: 'badge-new' };
  if (diffDays < 7) return { label: t('website.new_servers.badge_rising'), cls: 'badge-rising' };
  if (diffDays < 30) return { label: t('website.new_servers.badge_month'), cls: 'badge-month' };
  if (diffDays > 365) return { label: t('website.new_servers.badge_veteran'), cls: 'badge-veteran' };
  return null;
};

const timeAgo = (createdAt, t) => {
  if (!createdAt) return '';
  const diff = Date.now() - new Date(createdAt).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (days > 365) return t('website.new_servers.time_ago_years', { y: Math.floor(days / 365) });
  if (days > 30) return t('website.new_servers.time_ago_months', { m: Math.floor(days / 30) });
  if (days > 0) return t('website.new_servers.time_ago_days', { d: days });
  if (hours > 0) return t('website.new_servers.time_ago_hours', { h: hours });
  if (mins > 0) return t('website.new_servers.time_ago_mins', { m: mins });
  return t('website.new_servers.time_just_now');
};

const getFameBadge = (server, t) => {
  if (server.fame === 'famous') return { label: t('website.home.fame_famous'), icon: <FaCrown />, cls: 'famous' };
  if (server.fame === 'known') return { label: t('website.home.fame_known'), icon: <FaStar />, cls: 'known' };
  if (server.fame === 'unknown') return { label: t('website.home.fame_unknown'), cls: 'unknown' };
  return null;
};

const FILTER_KEYS = [
  { key: 'newest', icon: <FiZap /> },
  { key: 'top_rated', icon: <FiStar /> },
  { key: 'today', icon: <FiClock /> },
  { key: 'this_week', icon: <FiCalendar /> },
  { key: 'arabic', icon: <MdLanguage /> },
  { key: 'foreign', icon: <MdPublic /> },
];

const NewServers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: serversData, isLoading } = useSelector(state => state.servers);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'archive' ? 'archive' : 'servers');
  const [activeFilter, setActiveFilter] = useState('newest');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('grid');
  const [page, setPage] = useState(1);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'archive' ? { tab: 'archive' } : {});
  };

  useEffect(() => {
    dispatch(getServers({ page: 1, size: 50, query: '', filter: JSON.stringify({ is_active: true }) }));
  }, [dispatch]);

  const allServers = serversData?.data || [];

  const filterLabel = (key) => {
    const map = {
      newest: t('website.new_servers.filter_newest'),
      top_rated: t('website.new_servers.filter_top_rated'),
      today: t('website.new_servers.filter_today'),
      this_week: t('website.new_servers.filter_this_week'),
      arabic: t('website.new_servers.filter_arabic'),
      foreign: t('website.new_servers.filter_foreign'),
    };
    return map[key] || key;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? allServers.filter(s => s.name.toLowerCase().includes(q)) : allServers;
    if (activeFilter === 'top_rated') return [...list].sort((a, b) => b.votes - a.votes);
    if (activeFilter === 'arabic') return list.filter(s => !s.server_language || s.server_language === 'arabic');
    if (activeFilter === 'foreign') return list.filter(s => s.server_language === 'foreign');
    return list;
  }, [search, activeFilter, allServers]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageServers = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const goTo = p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const getImage = (server) => server.image ? `${backendUrl}/servers/${server.image}` : bannerGif;

  return (
    <WebsiteLayout>
      <PageWrapper>
        <HeroHeader>
          <div className="header-top">
            <div className="title-block">
              <div className="label"><span className="dot" /> {t('website.new_servers.updating')}</div>
              <div className="page-title"><BsDisplay /> {t('website.new_servers.title')}</div>
              <div className="page-sub">{t('website.new_servers.subtitle')}</div>
            </div>
            <Link to="/add-server" className="add-server-btn">
              <FiPlus /> {t('website.new_servers.add_server_btn', 'أضف سيرفرك')}
            </Link>
          </div>
          <div className="stats-strip">
            <div className="stat-item">
              <div className="stat-icon" style={{ background: 'rgba(147,51,234,0.18)', color: '#a78bfa' }}><BsDisplay /></div>
              <div className="stat-text">
                <div className="stat-value">{allServers.length}</div>
                <div className="stat-label">{t('website.new_servers.total_servers')}</div>
              </div>
            </div>
          </div>
        </HeroHeader>

        <TabBar>
          <button className={activeTab === 'servers' ? 'active' : ''} onClick={() => changeTab('servers')}>
            <BsDisplay /> {t('website.new_servers.tab_servers', 'السيرفرات')}
          </button>
          <button className={activeTab === 'archive' ? 'active' : ''} onClick={() => changeTab('archive')}>
            <BsArchiveFill /> {t('website.new_servers.tab_archive', 'الأرشيف')}
          </button>
        </TabBar>

        {activeTab === 'archive' ? <Archive /> : (
          <>
            <ControlsBar>
              <div className="search-wrap">
                <FiSearch />
                <input type="text" placeholder={t('website.new_servers.search_placeholder')} value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
              <div className="filter-group">
                {FILTER_KEYS.map(f => (
                  <FilterBtn key={f.key} active={activeFilter === f.key}
                    onClick={() => { setActiveFilter(f.key); setPage(1); }}>
                    {f.icon} {filterLabel(f.key)}
                  </FilterBtn>
                ))}
              </div>
              <div className="view-toggle">
                <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}><BsGrid /></button>
                <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><BsList /></button>
              </div>
            </ControlsBar>

            {isLoading ? (
              <ServersGrid className={view === 'list' ? 'list-view' : ''}>
                {Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)}
              </ServersGrid>
            ) : pageServers.length === 0 ? (
              <NoData />
            ) : (
              <ServersGrid className={view === 'list' ? 'list-view' : ''}>
                {pageServers.map(server => (
                  <ServerCard key={server._id} as={Link} to={`/server/${server._id}`}
                    className={view === 'list' ? 'list-card' : ''}>
                    <div className="card-img-wrap">
                      <img src={getImage(server)} alt={server.name} />
                      {(() => { const b = getAgeBadge(server.createdAt, t); return b ? <span className={`age-badge ${b.cls}`}>{b.label}</span> : null; })()}
                      {server.createdAt && <span className="time-badge"><FiClock />{timeAgo(server.createdAt, t)}</span>}
                    </div>
                    <div className="card-body">
                      <div className="card-top-row">
                        <div className="card-right-col">
                          <div className="card-name">{server.name}</div>
                          <div className="card-stats">{t('website.new_servers.wars_farms', { wars: server.wars, farms: server.farms })}</div>
                          <div className="card-level">Lv. {server.level}</div>
                          {(() => {
                            const f = getFameBadge(server, t);
                            return f ? (
                              <div className="card-owner">
                                <span className="owner-label">صاحب السيرفر:</span>
                                <span className={`fame-badge ${f.cls}`}>{f.label}</span>
                              </div>
                            ) : null;
                          })()}
                          <span className="status-badge"><span className="online-dot" /> {t('website.new_servers.available')}</span>
                        </div>
                        <div className="card-left-col">
                          <div className="card-likes"><FaHeart /> {server.votes || 0}</div>
                          <span className="platform-icon"><FaDiscord /></span>
                        </div>
                      </div>
                      {server.website_url && (
                        <a href={server.website_url} target="_blank" rel="noreferrer" className="website-btn"
                          onClick={e => e.preventDefault()}>
                          <FiGlobe size={12} /> {t('website.new_servers.website_btn')}
                        </a>
                      )}
                    </div>
                  </ServerCard>
                ))}
              </ServersGrid>
            )}

            {!isLoading && totalPages > 1 && (
              <PaginationBar>
                <div className="page-info">
                  {t('website.new_servers.showing')} <span>{(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, filtered.length)}</span> {t('website.new_servers.of')} <span>{filtered.length}</span> {t('website.new_servers.server_unit')}
                </div>
                <div className="pages">
                  <PageBtn disabled={currentPage === 1} onClick={() => goTo(currentPage - 1)}><FiChevronRight /></PageBtn>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <PageBtn key={n} active={n === currentPage} onClick={() => goTo(n)}>{n}</PageBtn>
                  ))}
                  <PageBtn disabled={currentPage === totalPages} onClick={() => goTo(currentPage + 1)}><FiChevronLeft /></PageBtn>
                </div>
              </PaginationBar>
            )}
          </>
        )}
      </PageWrapper>
    </WebsiteLayout>
  );
};

export default NewServers;
